import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  IconButton,
  Drawer,
} from "@mui/material";
import {
  GoogleMap,
  Marker,
  Polyline,
  Polygon,
  useJsApiLoader,
} from "@react-google-maps/api";
import dayjs, { Dayjs } from "dayjs";
import Grid from "@mui/material/Grid2";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTheme } from "@mui/material";
import VehicleList from "../../components/Vehicles/Vehicles";
import Header from "../../components/header";
import RightPanel from "../../components/RightPanel";
import CameraSection from "../../components/recording-timeline/CameraSection";
import VehicleDashboard from "../../components/VehicleDashboard";
import VideoHandlerCctv from "../../components/video-controller/cctv";
import {
  getDeviceAvailableDates,
  getAllDevicesData,
  getDeviceWiseAnalatics,
  getDeviceAiSeries,
  getDeviceAiRange,
  getDeviceWiseZoneData,
  getDeviceAiRegionCounts,
  getPedZoneData,
  getLineDataDeviceWise,
  getLineDetails,
  getVideoListData,
  deleteDeviceLine,
  getDeviceTTCData,
  getDevicePETData,
  editLineZoneWise,
  getDeviceTtcDataCount,
  getDevicePetDataCount
} from "../../redux/actions/service-action";
import {
  formatCount,
  formatDateTimeRange,
  formatFleetData,
  getFormattedDate,
} from "../../utils/methods";
import {
  setActiveCarDetails,
  setSelectedDate,
  setDateRange,
} from "../../redux/reducers/service-reducer";
import { RootState } from "../../redux/reducers";
import { mapStyle } from "../../utils/map-style";
import AddPlayer from "../../components/advertisement/AddPlayer";
import BlockBackButton from "../../utils/disabledBackButton";
import DragIndicatorIcon from '@mui/icons-material/Height';

const MAP_LIBRARIES: ("geometry")[] = ["geometry"];

const POLE_LOCATIONS: Record<string, { lat: number; lng: number }> = {
  p2: { lat: 28.471742, lng: 77.187 },
  p4: { lat: 28.488065, lng: 77.186359 },
  p5: { lat: 28.534035, lng: 77.231842 },
  // Keyed by system_id too, so renaming a device does not drop its marker.
  "7336c486-e00c-46ed-bad0-36f3ad9912e1": { lat: 28.471742, lng: 77.187 },
  "ed5ec77a-d3a1-4aa6-9474-72bd4af22892": { lat: 28.488065, lng: 77.186359 },
  "81d4b3d2-2630-4fd2-9fc9-2d922624db4e": { lat: 28.534035, lng: 77.231842 },
};

// Last fleet list seen, so a remount shows devices at once instead of 0/0
// while the devices API answers.
let fleetCache: any = {};

const Dashboard = () => {
  BlockBackButton("");
  const { deviceId, userId }: any = useParams();
  const dispatch = useDispatch();
  const { activeCarDetails, selectedDate } = useSelector(
    (state: RootState) => state.service
  );
  // Multi-day range: graph, line totals and snapshots only, no video.
  const dateRange = useSelector((state: RootState) => state.service.dateRange);
  const view = useSelector((state: any) => state.ui?.view || "Dashboard");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const allState = useSelector((state: any) => state);
  const [activeTab, setActiveTabData] = useState("dashboard");
  const [activeSlot, setActiveSlot] = useState("lines");

  const center = useMemo(
    () => ({
      lat: 28.534035,
      lng: 77.231842,
    }),
    []
  );
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [fleetData, setFleetData] = useState<any>(fleetCache);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [videoPlayStatus, setVideoPlayStatus] = useState(false);
  const [selectedZone, setSelectedZone] = useState("");
  const [videoData, setVideoData] = useState<any[]>([]);
  const [videoPlaySec, setVideoPlaySec] = useState<any>();
  const [openVehicleMenu, setOpenVehicleMenu] = useState(false);
  const [chartLoader, setChartLoader] = useState(false);
  const [zoneData, setZoneData] = useState([]);
  const [pedZoneData, setPedZoneData] = useState<any>(null);
  const [lineData, setLineData] = useState<any[]>([]);
  const [tempData, setTempData] = useState<any>([]);
  const [previousApiLinesData, setPreviousApiLinesData] = useState<any[]>([]);
  const [rawApiLines, setRawApiLines] = useState<any>([]);
  const [deviceAnalaticsData, setDeviceAnalaticsData] = useState<any>([]);
  // person counts scoped to the pedestrian crossing, for the "All" view
  const [pedSeries, setPedSeries] = useState<any>(null);

  // Each device publishes its own tunnel; never hardcode one host, or picking
  // a different camera silently charts this one's data.
  const deviceTunnelUrl = useMemo(
    () => (activeCarDetails?.raw?.tunnel_info || [])[0] || "",
    [activeCarDetails]
  );
  const getAlarmaAddress = async (e: any) => { };
  const refreshKey = 0;
  const [adsData, setAdsData] = useState<any[]>([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(-1);
  const [chartType, setChartType] = useState("default");
  const [ttcCount, setTtcCount] = useState<any>([]);
  const [petCount, setPetCount] = useState<any>([]);
  const isFetchingCountRef = useRef<string | false>(false);
  const [ttcData, setTtcData] = useState<any>(null);
  const [petData, setPetData] = useState<any>(null);
  const [isLineZoneData, setIsLineZoneData] = useState(false);
  const LEFT_PANEL_MIN_WIDTH = 2;
  const LEFT_PANEL_MAX_WIDTH = 3;
  const [leftPanelWidth, setLeftPanelWidth] = useState(LEFT_PANEL_MIN_WIDTH);
  const activeCarUid = useSelector(
    (s: any) => s.service.activeCarDetails?.raw?.uuid
  );
  const [drawingMode, setDrawingMode] = useState<"line" | "zone" | null>(null);
  const [mapDisplayType, setMapDisplayType] = useState<"roadmap" | "satellite">("roadmap");
  // "distance" = polyline measuring, "area" = polygon measuring, null = off
  const [measureType, setMeasureType] = useState<"distance" | "area" | null>(null);
  const [measurePath, setMeasurePath] = useState<google.maps.LatLngLiteral[]>([]);
  const [measureResult, setMeasureResult] = useState<string | null>(null);
  const [isMeasureMode, setIsMeasureMode] = useState(false);
  const activeMeasureShapeRef = useRef<google.maps.Polyline | google.maps.Polygon | null>(null);
  const [lines, setLines] = useState<number[][]>([]);
  const deviceNo = activeCarDetails?.deviceno;
  const tunnelUrl = activeCarDetails?.raw?.tunnel_info?.[0];
  const isMapView = view === "Map";
  const isFetchingAnalyticsRef = useRef<string | false>(false);
  const latestAnalyticsKeyRef = useRef("");
  const isFetchingZoneRef = useRef<string | false>(false);
  const isFetchingRef = useRef(false);
  const isFetchingLineRef = useRef<string | false>(false);
  const isFetchingVideoRef = useRef<string | false>(false);
  const lastFetchKeyRef = useRef("");
  // Identifies what the panels are showing. A response that resolves after
  // the user moved to another device or date belongs to the old one and is
  // dropped, otherwise a slow board answers into the new device's panels.
  const deviceKey = `${activeCarUid || ""}|${
    selectedDate ? dayjs(selectedDate).format("YYYY-MM-DD") : ""
  }`;
  const deviceKeyRef = useRef(deviceKey);
  deviceKeyRef.current = deviceKey;
  const isStale = (key: string) => deviceKeyRef.current !== key;
  const isDraggingLeft = useRef(false);
  const dragStartXLeft = useRef(0);
  const dragStartWidthLeft = useRef(0);
  const [fullDayDataCount, setFullDayData] = useState(0);
  const locations = useMemo(() => {
    const devices = fleetData?.Delhi?.fleet_devices || [];
    return devices
      .map((d: any) => {
        const pos =
          POLE_LOCATIONS[String(d.deviceno || "").toLowerCase()] ||
          POLE_LOCATIONS[String(d.devicename || "").toLowerCase()];
        if (!pos) return null;
        const isActive = d.deviceno === activeCarDetails?.deviceno;
        return {
          ...pos,
          device: d,
          active: isActive,
          count: isActive ? formatCount(fullDayDataCount) : null,
        };
      })
      .filter(Boolean);
  }, [fleetData, activeCarDetails, fullDayDataCount]);

  const handleLeftDragStart = (e: React.MouseEvent) => {
    isDraggingLeft.current = true;
    dragStartXLeft.current = e.clientX;
    dragStartWidthLeft.current = leftPanelWidth;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const onMouseMove = (ev: MouseEvent) => {
      if (!isDraggingLeft.current) return;
      const delta = ev.clientX - dragStartXLeft.current;
      const deltaUnits = (delta / window.innerWidth) * 12;
      const newWidth = Math.min(
        LEFT_PANEL_MAX_WIDTH,
        Math.max(LEFT_PANEL_MIN_WIDTH, dragStartWidthLeft.current + deltaUnits)
      );
      setLeftPanelWidth(newWidth);
    };
    const onMouseUp = () => {
      isDraggingLeft.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const [rightPanelWidth, setRightPanelWidth] = useState(2);
  const isDraggingRight = useRef(false);
  const dragStartXRight = useRef(0);
  const dragStartWidthRight = useRef(0);

  const handleRightDragStart = (e: React.MouseEvent) => {
    isDraggingRight.current = true;
    dragStartXRight.current = e.clientX;
    dragStartWidthRight.current = rightPanelWidth;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const onMouseMove = (ev: MouseEvent) => {
      if (!isDraggingRight.current) return;
      const delta = ev.clientX - dragStartXRight.current;
      const deltaUnits = (-delta / window.innerWidth) * 12;
      const newWidth = Math.min(
        5,
        Math.max(1.5, dragStartWidthRight.current + deltaUnits)
      );
      setRightPanelWidth(newWidth);
    };
    const onMouseUp = () => {
      isDraggingRight.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyMargin = document.body.style.margin;
    const prevBodyHeight = document.body.style.height;
    if (isMobile) {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.margin = prevBodyMargin;
      document.body.style.height = "";
    } else {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      document.body.style.margin = "0";
      document.body.style.height = "100vh";
    }
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.margin = prevBodyMargin;
      document.body.style.height = prevBodyHeight;
    };
  }, [isMobile]);

  const handleDateChange = async (newValue: Dayjs | null) => {
    dispatch(setSelectedDate(newValue));
  };

  const handleRangeChange = (range: { start: string; end: string }) => {
    dispatch(setDateRange(range));
  };

  const handlePressCar = (e: any) => {
    dispatch(setActiveCarDetails(e));
  };

  // const { isLoaded } = useJsApiLoader({
  //   id: "google-map-script",
  //   googleMapsApiKey: `${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
  //   libraries: MAP_LIBRARIES,
  // });

  const LIBRARIES : ("geometry")[] = ["geometry"];

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: `${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
    libraries: LIBRARIES as any,
  });

  const options: any = useMemo(
    () => ({
      disableDefaultUI: true,
      zoomControl: true,
      zoom: 15,
      mapTypeId: mapDisplayType,
      styles: mapDisplayType === "roadmap" ? mapStyle : undefined,
    }),
    [mapDisplayType]
  );

  const clearMeasurement = useCallback(() => {
    setMeasureType(null);
    setMeasurePath([]);
    setMeasureResult(null);
  }, []);

  const startMeasure = (type: "distance" | "area") => {
    setMeasureType(type);
    setMeasurePath([]);
    setMeasureResult(null);
  };

  // Called on every map click while a measure mode is active — adds a vertex
  const handleMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!measureType || !e.latLng) return;
      const point = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      setMeasurePath((prev) => [...prev, point]);
    },
    [measureType]
  );

  // Recompute live result whenever the path changes
  useEffect(() => {
    if (!measureType || !window.google?.maps?.geometry) return;

    if (measureType === "distance") {
      if (measurePath.length < 2) {
        setMeasureResult(null);
        return;
      }
      const path = measurePath.map(
        (p) => new window.google.maps.LatLng(p.lat, p.lng)
      );
      const distanceMeters = window.google.maps.geometry.spherical.computeLength(path);
      setMeasureResult(
        distanceMeters >= 1000
          ? `Distance: ${(distanceMeters / 1000).toFixed(2)} km`
          : `Distance: ${distanceMeters.toFixed(1)} m`
      );
    }

    if (measureType === "area") {
      if (measurePath.length < 3) {
        setMeasureResult(null);
        return;
      }
      const path = measurePath.map(
        (p) => new window.google.maps.LatLng(p.lat, p.lng)
      );
      const areaSqMeters = window.google.maps.geometry.spherical.computeArea(path);
      setMeasureResult(
        areaSqMeters >= 1_000_000
          ? `Area: ${(areaSqMeters / 1_000_000).toFixed(3)} km²`
          : `Area: ${areaSqMeters.toFixed(1)} m²`
      );
    }
  }, [measurePath, measureType]);

  // Remove last placed point (handy for misclicks)
  const undoLastPoint = () => {
    setMeasurePath((prev) => prev.slice(0, -1));
  };

  const buildDotIcon = (online: boolean) => {
    const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22">
      <circle cx="11" cy="11" r="8" fill="${online ? "#ff6b00" : "#9e9e9e"}"
        stroke="#fff" stroke-width="3" />
    </svg>`;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new window.google.maps.Size(22, 22),
      anchor: new window.google.maps.Point(11, 11),
    };
  };

  const buildCountIcon = (count: string | number) => {
    const text = String(count);
    // width scales a bit with text length so longer counts don't get clipped
    const width = Math.max(50, text.length * 14 + 24);
    const height = 34;
    const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
      <rect x="2" y="2" width="${width - 4}" height="${height - 4}" rx="${(height - 4) / 2}"
        fill="#ff6b00" stroke="#bfbfbf" stroke-width="3" />
      <text x="50%" y="52%" text-anchor="middle" dominant-baseline="middle"
        font-family="Arial, sans-serif" font-size="15" font-weight="bold" fill="#fff">
        ${text}
      </text>
    </svg>`;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new window.google.maps.Size(width, height),
      anchor: new window.google.maps.Point(width / 2, height / 2), // centers the icon on the coordinate
    };
  };

  const handleMeasurePolylineComplete = useCallback((polyline: google.maps.Polyline) => {
    clearMeasurement();
    activeMeasureShapeRef.current = polyline;
    const path = polyline.getPath();
    const distanceMeters = window.google.maps.geometry.spherical.computeLength(path.getArray());
    const label =
      distanceMeters >= 1000
        ? `Distance: ${(distanceMeters / 1000).toFixed(2)} km`
        : `Distance: ${distanceMeters.toFixed(1)} m`;
    setMeasureResult(label);
  }, [clearMeasurement]);

  const handleMeasurePolygonComplete = useCallback((polygon: google.maps.Polygon) => {
    clearMeasurement();
    activeMeasureShapeRef.current = polygon;
    const path = polygon.getPath();
    const areaSqMeters = window.google.maps.geometry.spherical.computeArea(path.getArray());
    const label =
      areaSqMeters >= 1_000_000
        ? `Area: ${(areaSqMeters / 1_000_000).toFixed(3)} km²`
        : `Area: ${areaSqMeters.toFixed(1)} m²`;
    setMeasureResult(label);
  }, [clearMeasurement]);

  const toggleMeasureMode = () => {
    setIsMeasureMode((prev) => {
      if (prev) clearMeasurement();
      return !prev;
    });
  };

  useEffect(() => {
    // A line picked on one device rarely exists on the next (p5 has line1,
    // P4 line21); asking the new board for it returns an empty chart.
    setSelectedZone("");
    setDeviceAnalaticsData([]);
    setPedSeries(null);
    setTtcCount([]);
    setPetCount([]);
    setZoneData([]);
    setPedZoneData(null);
    setTtcData(null);
    setPetData(null);
    setTempData([]);
    setPreviousApiLinesData([]);
    setRawApiLines([]);
  }, [activeCarUid]);

  // The device whose dates the calendar should show; an answer for a device
  // the user has already left is dropped.
  const calUidRef = useRef<string | undefined>(activeCarUid);
  calUidRef.current = activeCarUid;
  const getCalAvailableDates = useCallback(async (ip: string) => {
    if (!ip) return;
    try {
      const res = await getDeviceAvailableDates(ip);
      if (calUidRef.current !== ip) return;
      if (Array.isArray(res?.dates)) {
        const formattedDates = res.dates.map((date: string) =>
          dayjs(date).format("YYYY-MM-DD")
        );
        setAvailableDates(formattedDates);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (activeCarUid) {
      setAvailableDates([]);
      getCalAvailableDates(activeCarUid);
    }
  }, [activeCarUid, getCalAvailableDates]);

  // The token arrives with the persisted auth state, which can hydrate after
  // the first render; firing once on mount sent this with no token at all.
  const authToken =
    allState?.auth?.loginDetails?.user_details?.[0]?.access_token;

  const getAllDevicesDataFunTest = useCallback(async () => {
    if (!authToken) return;
    try {
      const res: any = await getAllDevicesData(authToken);
      const formattedData: any = formatFleetData(res, "Delhi");
      fleetCache = formattedData;
      setFleetData(formattedData);
    } catch (error) {
      console.log(error);
    }
  }, [authToken]);

  useEffect(() => {
    getAllDevicesDataFunTest();
  }, [getAllDevicesDataFunTest]);

  const getDeviceWiseAnalaticsData = useCallback(async () => {
    if (!selectedDate || !deviceNo) return;
    // Keyed on the line too: a line picked while another line's series is
    // still loading must fetch its own, and the older answer must be dropped.
    const devKey = deviceKeyRef.current;
    const key = `${devKey}#${selectedZone || ""}`;
    latestAnalyticsKeyRef.current = key;
    const stale = () => isStale(devKey) || latestAnalyticsKeyRef.current !== key;
    if (isFetchingAnalyticsRef.current === key) return;
    try {
      isFetchingAnalyticsRef.current = key;
      setChartLoader(true);
      const selectedDeviceDate = formatDateTimeRange(selectedDate);
      const seriesStart = selectedDeviceDate?.start_datetime?.replace(" ", "T") + ":00";
      const seriesEnd = selectedDeviceDate?.end_datetime?.replace(" ", "T") + ":59";
      const res: any = await getDeviceAiSeries({
        statDate: seriesStart,
        endDate: seriesEnd,
        region: selectedZone || "",
        uid: activeCarUid,
      });
      if (stale()) return;
      if (res) {
        setDeviceAnalaticsData(res);
        // "All" shows the pedestrian-crossing count in its person chip, so the
        // person series has to come from that region too -- unscoped counts
        // person on every line, which is a different number.
        const ped: any = selectedZone
          ? null
          : await getDeviceAiSeries({
              statDate: seriesStart,
              endDate: seriesEnd,
              region: "ped_cross",
              uid: activeCarUid,
            });
        if (stale()) return;
        setPedSeries(ped || null);
        // Keep the chosen zone only when this device/date actually has it;
        // otherwise the old name sticks and the chart reads empty. The board's
        // ai-series carries no region map, so only check when one is present
        // -- an empty map used to snap every picked line back to "All".
        const zones = Object.keys(res?.class_counts_by_region || {});
        if (zones.length) {
          setSelectedZone((prev) =>
            prev && zones.includes(prev) ? prev : zones[0]
          );
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setChartLoader(false);
      if (isFetchingAnalyticsRef.current === key) isFetchingAnalyticsRef.current = false;
    }
  }, [selectedDate, selectedZone, activeCarUid, deviceNo]);

  useEffect(() => {
    getDeviceWiseAnalaticsData();
  }, [getDeviceWiseAnalaticsData]);

  const getDeviceTtcCount = useCallback(async () => {
    if (!selectedDate || !deviceNo) return 0;
    const key = deviceKeyRef.current;
    const selectedDeviceDate = formatDateTimeRange(selectedDate);
    const res: any = await getDeviceTtcDataCount({
      statDate: selectedDeviceDate?.start_datetime?.replace(" ", "T") + ":00",
      endDate: selectedDeviceDate?.end_datetime?.replace(" ", "T") + ":59",
      uid: activeCarUid,
    });
    if (res && !isStale(key)) {
      setTtcCount(res);
    }
    return res;
  }, [selectedDate, deviceNo, activeCarUid]);

  const getDevicePetCount = useCallback(async () => {
    if (!selectedDate || !deviceNo) return 0;
    const key = deviceKeyRef.current;
    const selectedDeviceDate = formatDateTimeRange(selectedDate);
    const res: any = await getDevicePetDataCount({
      statDate: selectedDeviceDate?.start_datetime?.replace(" ", "T") + ":00",
      endDate: selectedDeviceDate?.end_datetime?.replace(" ", "T") + ":59",
      uid: activeCarUid,
    });
    if (res && !isStale(key)) {
      setPetCount(res);
    }
    return res;
  }, [selectedDate, deviceNo, activeCarUid]);

  useEffect(() => {
    if (!selectedDate || !deviceNo) return;
    const fetchBothCount = async () => {
      const key = deviceKeyRef.current;
      if (isFetchingCountRef.current === key) return;
      try {
        isFetchingCountRef.current = key;
        await Promise.all([
          getDeviceTtcCount(),
          getDevicePetCount(),
        ]);
      } catch (error) {
        console.error(error);
      } finally {
        if (isFetchingCountRef.current === key) isFetchingCountRef.current = false;
      }
    };
    fetchBothCount();
    const interval = setInterval(fetchBothCount, 180000); // 3 minutes
    return () => clearInterval(interval);
  }, [selectedDate, deviceNo, getDeviceTtcCount, getDevicePetCount]);


  const zoneEndSecondsRef = useRef<number | null>(null);

  const getDeviceWiseZone = useCallback(
    async (endSeconds: number, reset = false) => {
      if (!selectedDate || !deviceNo || !activeCarUid) return;
      const key = deviceKeyRef.current;
      if (isFetchingZoneRef.current === key) return;
      // Only show the loader for the initial/reset fetch. Periodic 5s refetches
      // run in the background and keep the previous data on the right panel
      // until the fresh response arrives.
      if (reset) setIsLineZoneData(true);
      try {
        isFetchingZoneRef.current = key;
        const dayStart = dayjs(selectedDate).startOf("day");
        // Per-line totals from the server, deduplicated there, so they match
        // the board's own table and the chart -- and still load while the
        // device is offline.
        const res: any = await getDeviceAiRegionCounts({
          statDate: dayStart.format("YYYY-MM-DDTHH:mm:ss"),
          endDate: dayStart
            .add(endSeconds, "second")
            .format("YYYY-MM-DDTHH:mm:ss"),
          uid: activeCarUid,
        });
        if (res && !isStale(key)) {
          setZoneData((prev: any) =>
            reset ? res : { ...prev, ...res }
          );
        }
      } catch (error) {
        console.log(error);
      } finally {
        if (isFetchingZoneRef.current === key) isFetchingZoneRef.current = false;
        if (reset) setIsLineZoneData(false);
      }
    },
    [selectedDate, deviceNo, activeCarUid]
  );

  useEffect(() => {
    if (!selectedDate || !deviceNo) return;
    // Whole-day totals, fetched once per date/device. They do not change as
    // the video plays, so following playback only cost the board a full-day
    // aggregate every five seconds -- on the same process that serves the
    // video, over the same 4G uplink.
    const dayEnd = dayjs(selectedDate)
      .endOf("day")
      .diff(dayjs(selectedDate).startOf("day"), "second");
    zoneEndSecondsRef.current = dayEnd;
    getDeviceWiseZone(dayEnd, true);
  }, [selectedDate, deviceNo, getDeviceWiseZone]);


  // ==================== PED-ZONE (bus stand people count) ====================
  const pedZoneEndSecondsRef = useRef<number | null>(null);
  const isFetchingPedZoneRef = useRef<string | false>(false);

  const getPedZone = useCallback(
    async (endSeconds: number) => {
      if (!selectedDate || !deviceNo || !activeCarUid) return;
      const key = deviceKeyRef.current;
      if (isFetchingPedZoneRef.current === key) return;
      try {
        isFetchingPedZoneRef.current = key;
        const dayStart = dayjs(selectedDate).startOf("day");
        const res: any = await getPedZoneData({
          statDate: dayStart.format("YYYY-MM-DDTHH:mm:ss"),
          endDate: dayStart
            .add(endSeconds, "second")
            .format("YYYY-MM-DDTHH:mm:ss"),
          uid: activeCarUid,
        });
        if (res && !isStale(key)) {
          setPedZoneData(res);
        }
      } catch (error) {
        console.log(error);
      } finally {
        if (isFetchingPedZoneRef.current === key) isFetchingPedZoneRef.current = false;
      }
    },
    [selectedDate, deviceNo, activeCarUid]
  );

  // Initial fetch on date/device change or after a click on the video/chart.
  useEffect(() => {
    if (!selectedDate || !deviceNo) return;
    const dayEnd = dayjs(selectedDate)
      .endOf("day")
      .diff(dayjs(selectedDate).startOf("day"), "second");
    pedZoneEndSecondsRef.current = dayEnd;
    getPedZone(dayEnd);
  }, [selectedDate, deviceNo, getPedZone]);


  const getVideoLineDetails = useCallback(async () => {
    if (!tunnelUrl) return;
    const key = deviceKeyRef.current;
    if (isFetchingLineRef.current === key) return;
    try {
      isFetchingLineRef.current = key;
      const res: any = await getLineDetails(tunnelUrl);
      if (isStale(key)) return;
      setTempData(res);
      setPreviousApiLinesData(res?.items || []);
      setRawApiLines(res);
    } catch (error) {
      console.error(error);
    } finally {
      if (isFetchingLineRef.current === key) isFetchingLineRef.current = false;
    }
  }, [tunnelUrl]);

  useEffect(() => {
    if (tunnelUrl) {
      getVideoLineDetails();
    }
  }, [tunnelUrl, getVideoLineDetails]);

  const fetchVideosDemo = useCallback(async () => {
    if (!tunnelUrl) return;
    const key = deviceKeyRef.current;
    if (isFetchingVideoRef.current === key) return;
    try {
      setVideoData([]);
      isFetchingVideoRef.current = key;
      const formattedDate = getFormattedDate(selectedDate);
      const activeTripVideo = await getVideoListData(
        formattedDate,
        activeCarUid,
        tunnelUrl
      );
      if (isStale(key)) return;
      setVideoData(activeTripVideo);
    } catch (error) {
      console.error(error);
    } finally {
      if (isFetchingVideoRef.current === key) isFetchingVideoRef.current = false;
    }
  }, [selectedDate, tunnelUrl, activeCarUid]);

  useEffect(() => {
    if (!selectedDate || !activeCarUid) return;
    const key = `${selectedDate}_${activeCarUid}`;
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    fetchVideosDemo();
  }, [selectedDate, activeCarUid, fetchVideosDemo]);

  const secHandler = (sec: string) => {
    setVideoPlaySec(sec);
  };

  const deleteLineZone = async (name: string) => {
    const type = name.startsWith("zone") ? "zone_occupancy" : "line_name";
    try {
      const res: any = await deleteDeviceLine(tunnelUrl, name, type);
      if (res) {
        getVideoLineDetails();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const editLineZone = async (
    lineName: string,
    updatedLinename: string
  ) => {
    try {
      const res = await editLineZoneWise(tunnelUrl, lineName, updatedLinename);
      if (res) {
        getVideoLineDetails();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const dragHandleBarSx = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "2px",
    height: "48px",
    borderRadius: "2px",
    background: "#bbb",
    opacity: 0,
    transition: "opacity 0.2s",
    pointerEvents: "none",
  };

  const dragHandleWrapSx = {
    width: "2px",
    minWidth: "2px",
    maxWidth: "2px",
    flex: "0 0 14px",
    cursor: "col-resize",
    background: "transparent",
    flexShrink: 0,
    zIndex: 10,
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover .drag-icon": {
      opacity: 1,
      background: "#ED6926",
      color: "#fff",
    },
  };

  const dragIconSx = {
    opacity: 0,
    transition: "opacity 0.2s, background 0.2s",
    color: "#666",
    fontSize: "25px",
    borderRadius: "4px",
    background: "#f0f0f0",
    border: "1px solid #ddd",
    transform: "rotate(90deg)",
    pointerEvents: "none",
  };

  const LeftPanelDragHandle = () =>
    !isCollapsed && !isMobile ? (
      <Box onMouseDown={handleLeftDragStart} sx={dragHandleWrapSx}>
        <DragIndicatorIcon className="drag-icon" sx={dragIconSx} />
        <Box sx={dragHandleBarSx} />
      </Box>
    ) : null;

  const RightPanelDragHandle = () =>
    !isMobile ? (
      <Box onMouseDown={handleRightDragStart} sx={dragHandleWrapSx}>
        <DragIndicatorIcon className="drag-icon" sx={dragIconSx} />
        <Box sx={dragHandleBarSx} />
      </Box>
    ) : null;

  const buildTtcPetZoneData = (apiResponse: any, type: "ttc" | "pet") => {
    const result: any = {
      class_counts_by_region: {},
    };
    if (!apiResponse?.events?.length) {
      return result;
    }
    apiResponse?.events?.forEach((item: any) => {
      const region = item.Direction || item.Lane_ID || "unknown";
      const vehicleClass = (
        item.Lead_Vehicle_Type ||
        item.class ||
        item.vehicle_class
      )?.toLowerCase();
      if (!vehicleClass) return;
      if (!result.class_counts_by_region[region]) {
        result.class_counts_by_region[region] = {};
      }
      result.class_counts_by_region[region][vehicleClass] =
        (result.class_counts_by_region[region][vehicleClass] || 0) + 1;
    });
    return result;
  };

  const lastRequestRef = useRef<string>("");

  const deviceTTCData = async () => {
    if (!selectedDate || !tunnelUrl) return;
    const key = deviceKeyRef.current;
    const formattedDate = formatDateTimeRange(selectedDate);
    try {
      const res: any = await getDeviceTTCData(activeCarDetails?.raw?.uuid, formattedDate);
      if (res && !isStale(key)) {
        setTtcData(res);
      }
    } catch (error) {
      console.error("TTC Error:", error);
    }
  };

  const devicePETData = async () => {
    if (!selectedDate || !tunnelUrl) return;
    const key = deviceKeyRef.current;
    const formattedDate = formatDateTimeRange(selectedDate);
    try {
      const res: any = await getDevicePETData(activeCarDetails?.raw?.uuid, formattedDate);
      if (res && !isStale(key)) {
        setPetData(res);
      }
    } catch (error) {
      console.error("PET Error:", error);
    }
  };

  useEffect(() => {
    if (!selectedDate || !tunnelUrl || !activeCarDetails?.deviceno) return;
    const formattedDate = getFormattedDate(selectedDate);
    const requestKey = `${tunnelUrl}_${formattedDate}_${activeCarDetails.deviceno}`;
    if (lastRequestRef.current === requestKey) {
      return;
    }
    lastRequestRef.current = requestKey;
    deviceTTCData();
    devicePETData();
  }, [tunnelUrl, selectedDate, activeCarDetails?.deviceno]);

  useEffect(() => {
    setSelectedZone("");
  }, [chartType]);

  const formatTtcPetForChart = (
    apiResponse: any,
    selectedZone: string,
    type: "ttc" | "pet"
  ) => {
    if (!apiResponse?.events?.length) {
      return {
        classes: [type],
        slots: [],
      };
    }

    const filteredData = selectedZone
      ? apiResponse.events.filter(
        (item: any) => item.Direction === selectedZone
      )
      : apiResponse.events;

    const grouped: Record<string, any> = {};

    filteredData.forEach((item: any) => {
      const time = dayjs(item.timeslot).format("YYYY-MM-DD HH:mm:00");

      if (!grouped[time]) {
        grouped[time] = {
          time,
          ttc: 0,
          pet: 0,
        };
      }

      if (type === "ttc") {
        grouped[time].ttc += 1;
      } else {
        grouped[time].pet += 1;
      }
    });

    return {
      classes: [type],
      slots: Object.values(grouped).sort(
        (a: any, b: any) =>
          new Date(a.time).getTime() - new Date(b.time).getTime()
      ),
    };
  };

  // ==================== MULTI-DAY RANGE ====================
  // One request returns hourly [time, region, class, count] rows for the whole
  // range; the graph, the per-line table and the pedestrian series are built
  // here from them, so picking another line needs no new request.
  const [rangeRows, setRangeRows] = useState<any[] | null>(null);
  const [rangeLoading, setRangeLoading] = useState(false);
  const rangeKey = dateRange ? `${activeCarUid || ""}|${dateRange.start}|${dateRange.end}` : "";
  const rangeKeyRef = useRef(rangeKey);
  rangeKeyRef.current = rangeKey;

  useEffect(() => {
    setRangeRows(null);
    if (!dateRange || !activeCarUid) {
      setRangeLoading(false);
      return;
    }
    const key = rangeKey;
    setRangeLoading(true);
    getDeviceAiRange({ startDate: dateRange.start, endDate: dateRange.end, uid: activeCarUid })
      .then((res: any) => {
        if (rangeKeyRef.current !== key) return;
        setRangeRows(res?.rows || []);
      })
      .finally(() => {
        if (rangeKeyRef.current === key) setRangeLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey]);

  // TTC/PET come from the board one day at a time; a range shows AI data only.
  useEffect(() => {
    if (dateRange && chartType !== "default") setChartType("default");
  }, [dateRange, chartType]);

  const rangeView = useMemo(() => {
    if (!dateRange || !rangeRows) return null;
    const series = (keep: (region: string) => boolean) => {
      const byTime = new Map<string, any>();
      const classes = new Set<string>();
      rangeRows.forEach(([time, region, cls, n]: any) => {
        if (!keep(region) || !cls) return;
        classes.add(cls);
        const row = byTime.get(time) || { time };
        row[cls] = (row[cls] || 0) + Number(n || 0);
        byTime.set(time, row);
      });
      const list = Array.from(classes).sort();
      const slots = Array.from(byTime.values())
        .sort((a, b) => (a.time < b.time ? -1 : 1))
        .map((row) => {
          list.forEach((c) => (row[c] = row[c] || 0));
          return row;
        });
      return { classes: list, slots, granularity: "hour" };
    };
    const byRegion: Record<string, Record<string, number>> = {};
    rangeRows.forEach(([, region, cls, n]: any) => {
      if (!cls) return;
      const r = (byRegion[region] = byRegion[region] || {});
      r[cls] = (r[cls] || 0) + Number(n || 0);
    });
    const chart = series((region) => !selectedZone || region === selectedZone);
    // On "All", person means the pedestrian crossing, as in day mode. A range
    // with no ped_cross rows still needs a (zero) series, or the chart would
    // fall back to person counts from every line.
    let ped: any = null;
    if (!selectedZone) {
      ped = series((region) => region === "ped_cross");
      if (!ped.slots.length) ped = { classes: [], slots: chart.slots.map((r: any) => ({ time: r.time, person: 0 })) };
    }
    return { chart, ped, zoneData: { class_counts_by_region: byRegion } };
  }, [dateRange, rangeRows, selectedZone]);

  // A line picked on another day or range may not exist in this range; fall
  // back to "All" instead of a silently empty chart.
  useEffect(() => {
    if (!dateRange || !rangeRows || !selectedZone) return;
    if (!rangeRows.some((r: any) => r[1] === selectedZone)) setSelectedZone("");
  }, [dateRange, rangeRows, selectedZone]);

  const activeChartData = useMemo(() => {
    if (dateRange) return rangeView?.chart || [];
    if (chartType === "ttc") {
      return formatTtcPetForChart(ttcData, selectedZone, "ttc");
    }
    if (chartType === "pet") {
      return formatTtcPetForChart(petData, selectedZone, "pet");
    }
    return deviceAnalaticsData;
  }, [chartType, selectedZone, deviceAnalaticsData, ttcData, petData, dateRange, rangeView]);

  const activeRightPanelData = useMemo(() => {
    if (dateRange) return rangeView?.zoneData || {};
    if (chartType === "ttc") {
      return buildTtcPetZoneData(ttcData, "ttc");
    }
    if (chartType === "pet") {
      return buildTtcPetZoneData(petData, "pet");
    }
    return zoneData;
  }, [chartType, zoneData, ttcData, petData, dateRange, rangeView]);
  const chartPedSeries = dateRange ? rangeView?.ped || null : pedSeries;
  const chartLoading = dateRange ? rangeLoading : chartLoader;

  return (
    <>
      <Header
        fleetData={fleetData}
        playingStatus={videoPlayStatus}
        profileDetails="PR"
        selectedDate={selectedDate}
        availableDates={availableDates}
        onDateChange={handleDateChange}
        onToggle={() => setIsCollapsed((prev) => !prev)}
        isCollapsed={isCollapsed}
        onMenuClick={() => setOpenVehicleMenu(true)}
        disableDateChange={chartLoading}
        dateRange={dateRange}
        onRangeChange={handleRangeChange}
      />
      {activeTab === "dashboard" && (
        <>
          <Drawer
            anchor="left"
            open={openVehicleMenu}
            onClose={() => setOpenVehicleMenu(false)}
            sx={{
              "& .MuiDrawer-paper": {
                width: "280px",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px",
                borderBottom: "1px solid #eee",
              }}
            >
              <Typography variant="h6">Vehicles</Typography>
              <IconButton onClick={() => setOpenVehicleMenu(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ padding: "10px" }}>
              <VehicleList
                fleetData={fleetData}
                onPressCar={(car) => {
                  handlePressCar(car);
                  setOpenVehicleMenu(false);
                }}
                isCollapsed={false}
                showSearch={view === "Search"}
                deviceId={deviceId}
                userId={userId}
                miceStatus={null}
              />
            </Box>
          </Drawer>
          <Grid
            container
            spacing={0}
            sx={{
              height: isMobile ? "auto" : "100vh",
              minHeight: isMobile ? "100vh" : undefined,
              p: 0,
              mt: "5px",
              overflow: isMobile ? "visible" : "hidden",
              boxSizing: "border-box",
            }}
          >
            <Grid size={{ xs: 12, md: isCollapsed ? 0 : leftPanelWidth }}>
              <Box sx={{ marginLeft: "45px", height: "100%", overflow: "hidden" }}>
                {!isMobile && (
                  <VehicleList
                    fleetData={fleetData}
                    onPressCar={(car) => {
                      handlePressCar(car);
                      setOpenVehicleMenu(false);
                    }}
                    isCollapsed={isCollapsed}
                    showSearch={view === "Search"}
                    deviceId={deviceId}
                    userId={userId}
                    miceStatus={null}
                  />
                )}
              </Box>
            </Grid>
            {!isMapView ? (
              <>
                <LeftPanelDragHandle />
                <Grid
                  size={
                    isCollapsed
                      ? { xs: 12, md: 11.9 }
                      : { xs: 12, md: 11.9 - leftPanelWidth }
                  }
                >
                  <Box
                    sx={{
                      // A fixed height, not 100%: the Grid row wraps, and a
                      // percentage height inside a wrapping flex row resolves
                      // to "auto" -- the column then grew with its content and
                      // never scrolled, while body overflow:hidden clipped the
                      // graph below the fold. 100vh minus the header row.
                      height: isMobile ? "auto" : "calc(100vh - 60px)",
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                      padding: "3px 12px 24px 15px",
                      overflowY: isMobile ? "visible" : "auto",
                      boxSizing: "border-box",
                      // Sections keep their own height and the column scrolls;
                      // flex children would otherwise shrink to fit instead.
                      "& > *": { flexShrink: 0 },
                    }}
                  >
                    <CameraSection
                      uuid={activeCarUid}
                      date={selectedDate ? dayjs(selectedDate).format("YYYY-MM-DD") : ""}
                      range={dateRange}
                    >
                    {!dateRange && (
                    <VideoHandlerCctv
                      key={`${activeCarDetails?.deviceno}${selectedDate}`}
                      onTimeChange={secHandler}
                      uuid={activeCarUid}
                      onAlarmDataChange={(details) => {
                        getAlarmaAddress(details);
                      }}
                      videoPlayStatus={setVideoPlayStatus}
                      videoListData={setVideoData}
                      lineData={lineData}
                      tempData={tempData}
                      previousApiLinesData={previousApiLinesData}
                      rawApiLines={rawApiLines}
                      videoData={videoData}
                      playVideoTime={videoPlaySec}
                      zoneData={zoneData}
                      pedZoneData={pedZoneData}
                      drawingMode={drawingMode}
                      setDrawingMode={setDrawingMode}
                      lines={lines}
                      setLines={setLines}
                      ttcCountData={ttcCount}
                      petCountData={petCount}
                    />
                    )}
                    </CameraSection>
                    <Box
                      sx={{
                        bgcolor: "#fdfdfd",
                        flex: "none",
                      }}
                    >
                      {activeCarDetails?.devicename && (
                        <VehicleDashboard
                          pedSeries={chartPedSeries}
                          apiData={activeChartData}
                          selectedZone={selectedZone}
                          loading={chartLoading}
                          onTimeSelect={dateRange ? undefined : secHandler}
                          currentTime={dateRange ? undefined : videoPlaySec}
                          range={dateRange}
                          handleFullDayCount={setFullDayData}
                          chartType={chartType}
                          setChartType={setChartType}
                          setSelectedZone={setSelectedZone}
                          zoneData={activeRightPanelData}
                          activeTabData={activeSlot}
                        />
                      )}
                    </Box>
                    <Box sx={{ border: "1px solid #e5e7eb", borderRadius: "8px", bgcolor: "#fff", flex: "none" }}>
                    <RightPanel
                      asTable
                      selectedZone={selectedZone}
                      setSelectedZone={setSelectedZone}
                      zoneData={activeRightPanelData}
                      setActiveTabData={setActiveTabData}
                      setActiveSlot={setActiveSlot}
                      drawingMode={drawingMode}
                      setDrawingMode={setDrawingMode}
                      lines={lines}
                      setLines={setLines}
                      deleteLineZone={deleteLineZone}
                      editLineZone={editLineZone}
                      chartType={chartType}
                      setChartType={setChartType}
                      totalCount={fullDayDataCount}
                      ttcTotal={ttcData?.count || 0}
                      petTotal={petData?.count || 0}
                      isLineZoneData={isLineZoneData}
                    />
                    </Box>
                  </Box>
                </Grid>
              </>
            ) : (
              <>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Box
                    sx={{
                      height: "100%",
                      bgcolor: "#f5f5f5",
                      padding: "0px",
                      overflow: "hidden",
                      boxSizing: "border-box",
                    }}
                  >
                    {isLoaded && (
                      <Box sx={{ position: "relative", height: "100%" }}>
                        <Box
                          sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            zIndex: 5,
                            display: "flex",
                            gap: "8px",
                            flexWrap: "wrap",
                            maxWidth: "95%",
                          }}
                        >
                          <button
                            onClick={() =>
                              setMapDisplayType((prev) => (prev === "roadmap" ? "satellite" : "roadmap"))
                            }
                            style={{
                              padding: "6px 10px",
                              borderRadius: "6px",
                              border: "1px solid #333",
                              background: "#fff",
                              color: "#000",
                              cursor: "pointer",
                              fontSize: "12px",
                              fontWeight: 600,
                            }}
                          >
                            {mapDisplayType === "roadmap" ? "Satellite View" : "Map View"}
                          </button>

                          <button
                            onClick={() => (measureType === "distance" ? clearMeasurement() : startMeasure("distance"))}
                            style={{
                              padding: "6px 10px",
                              borderRadius: "6px",
                              border: "1px solid #333",
                              background: measureType === "distance" ? "#ff6b00" : "#fff",
                              color: measureType === "distance" ? "#fff" : "#000",
                              cursor: "pointer",
                              fontSize: "12px",
                              fontWeight: 600,
                            }}
                          >
                            {measureType === "distance" ? "Stop Distance" : "Measure Distance"}
                          </button>

                          <button
                            onClick={() => (measureType === "area" ? clearMeasurement() : startMeasure("area"))}
                            style={{
                              padding: "6px 10px",
                              borderRadius: "6px",
                              border: "1px solid #333",
                              background: measureType === "area" ? "#ff6b00" : "#fff",
                              color: measureType === "area" ? "#fff" : "#000",
                              cursor: "pointer",
                              fontSize: "12px",
                              fontWeight: 600,
                            }}
                          >
                            {measureType === "area" ? "Stop Area" : "Measure Area"}
                          </button>

                          {measureType && measurePath.length > 0 && (
                            <button
                              onClick={undoLastPoint}
                              style={{
                                padding: "6px 10px",
                                borderRadius: "6px",
                                border: "1px solid #333",
                                background: "#fff",
                                color: "#000",
                                cursor: "pointer",
                                fontSize: "12px",
                                fontWeight: 600,
                              }}
                            >
                              Undo Point
                            </button>
                          )}

                          {measureResult && (
                            <Box
                              sx={{
                                padding: "6px 10px",
                                borderRadius: "6px",
                                background: "#fff",
                                color: "#000",
                                border: "1px solid #333",
                                fontSize: "12px",
                                fontWeight: 600,
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                              }}
                            >
                              {measureResult}
                              <span
                                onClick={clearMeasurement}
                                style={{ cursor: "pointer", color: "#999", fontWeight: 700 }}
                              >
                                ✕
                              </span>
                            </Box>
                          )}
                        </Box>

                        {measureType && (
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 8,
                              left: 8,
                              zIndex: 5,
                              background: "rgba(255,255,255,0.9)",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "11px",
                            }}
                          >
                            {measureType === "distance"
                              ? "Click on the map to add points along the path"
                              : "Click on the map to add corners of the area"}
                          </Box>
                        )}

                        <GoogleMap
                          options={options}
                          mapContainerStyle={{ width: "100%", height: "95%" }}
                          center={center}
                          onClick={handleMapClick}
                        >
                          {locations?.map((item: any, index: number) => (
                            <Marker
                              key={item.device?.deviceno || index}
                              position={{ lat: item.lat, lng: item.lng }}
                              icon={
                                item.active
                                  ? buildCountIcon(item.count)
                                  : buildDotIcon(item.device?.status === "online")
                              }
                              zIndex={item.active ? 999 : 1}
                              title={item.device?.devicename}
                              onClick={() => handlePressCar(item.device)}
                            />
                          ))}

                          {measureType === "distance" && measurePath.length > 0 && (
                            <Polyline
                              path={measurePath}
                              options={{
                                strokeColor: "#ff6b00",
                                strokeWeight: 3,
                              }}
                            />
                          )}

                          {measureType === "area" && measurePath.length > 0 && (
                            <Polygon
                              path={measurePath}
                              options={{
                                fillColor: "#ff6b00",
                                fillOpacity: 0.25,
                                strokeColor: "#ff6b00",
                                strokeWeight: 2,
                              }}
                            />
                          )}
                        </GoogleMap>
                      </Box>
                    )}
                  </Box>
                </Grid>

                <Grid
                  size={isCollapsed ? { xs: 12, md: 9 } : { xs: 12, md: 7 }}
                >
                  <Box
                    sx={{
                      // Same fix as the dashboard view: a fixed height so the
                      // column scrolls instead of clipping the chart.
                      height: isMobile ? "auto" : "calc(100vh - 22px)",
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                      padding: "40px 0px 24px 10px",
                      marginTop: "-38px",
                      overflowY: isMobile ? "visible" : "auto",
                      boxSizing: "border-box",
                      "& > *": { flexShrink: 0 },
                    }}
                  >
                    {dateRange ? (
                      <Box sx={{ p: 3, textAlign: "center", color: "#6b7280", fontSize: 13, bgcolor: "#f3f4f6", borderRadius: "8px" }}>
                        Video plays one day at a time. Pick a single day to watch it.
                      </Box>
                    ) : (
                    <VideoHandlerCctv
                      key={`${activeCarDetails?.deviceno}${selectedDate}`}
                      onTimeChange={secHandler}
                      uuid={activeCarUid}
                      onAlarmDataChange={(details) => {
                        getAlarmaAddress(details);
                      }}
                      videoPlayStatus={setVideoPlayStatus}
                      videoListData={setVideoData}
                      lineData={lineData}
                      tempData={tempData}
                      previousApiLinesData={previousApiLinesData}
                      rawApiLines={rawApiLines}
                      videoData={videoData}
                      playVideoTime={videoPlaySec}
                      zoneData={zoneData}
                      pedZoneData={pedZoneData}
                      drawingMode={drawingMode}
                      setDrawingMode={setDrawingMode}
                      lines={lines}
                      setLines={setLines}
                      ttcCountData={ttcCount}
                      petCountData={petCount}
                    />
                    )}
                    <Box sx={{ flex: "none" }}>
                      {activeCarDetails?.devicename && (
                        <VehicleDashboard
                          pedSeries={chartPedSeries}
                          apiData={activeChartData}
                          selectedZone={selectedZone}
                          loading={chartLoading}
                          onTimeSelect={dateRange ? undefined : secHandler}
                          currentTime={dateRange ? undefined : videoPlaySec}
                          range={dateRange}
                          handleFullDayCount={setFullDayData}
                          chartType={chartType}
                          setChartType={setChartType}
                          setSelectedZone={setSelectedZone}
                          zoneData={activeRightPanelData}
                          activeTabData={activeSlot}
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </>
      )}
      {activeTab === "advertisment" && (
        <>
          <Drawer
            anchor="left"
            open={openVehicleMenu}
            onClose={() => setOpenVehicleMenu(false)}
            sx={{
              "& .MuiDrawer-paper": {
                width: "280px",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px",
                borderBottom: "1px solid #eee",
              }}
            >
              <Typography variant="h6">Vehicles</Typography>
              <IconButton onClick={() => setOpenVehicleMenu(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ padding: "10px" }}>
              <VehicleList
                fleetData={fleetData}
                onPressCar={(car) => {
                  handlePressCar(car);
                  setOpenVehicleMenu(false);
                }}
                isCollapsed={false}
                showSearch={view === "Search"}
                deviceId={deviceId}
                userId={userId}
                miceStatus={null}
              />
            </Box>
          </Drawer>
          <Grid
            container
            spacing={0}
            sx={{
              height: "100vh",
              p: 0,
              marginTop: "5px",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <Grid size={{ xs: 12, md: isCollapsed ? 0.5 : 2.2 }}>
              <Box sx={{ height: "100%", overflow: "hidden", marginLeft: "45px" }}>
                {!isMobile && (
                  <VehicleList
                    fleetData={fleetData}
                    onPressCar={(car) => {
                      handlePressCar(car);
                      setOpenVehicleMenu(false);
                    }}
                    isCollapsed={isCollapsed}
                    showSearch={view === "Search"}
                    deviceId={deviceId}
                    userId={userId}
                    miceStatus={null}
                  />
                )}
              </Box>
            </Grid>
            <LeftPanelDragHandle />
            <Grid
              size={
                isCollapsed
                  ? { xs: 12, md: 8.5 }
                  : { xs: 12, md: 11.5 - leftPanelWidth - rightPanelWidth }
              }
            >
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  padding:
                    activeTab === "advertisment"
                      ? "3px 10px 0px 0px"
                      : "3px 0px 0px 23px",
                  overflow: "hidden",
                  boxSizing: "border-box",
                }}
              >
                <div>
                  <AddPlayer
                    key={refreshKey}
                    onAdsDataChange={setAdsData}
                    currentAdIndex={currentAdIndex}
                    setCurrentAdIndex={setCurrentAdIndex}
                  />
                </div>
              </Box>
            </Grid>
            <RightPanelDragHandle />
            <Grid size={{ xs: 12, md: 2.2 }}>
              <Box
                sx={{
                  height: isMobile ? "auto" : "98%",
                  bgcolor: "#f5f5f5",
                  padding: "0px",
                  overflow: isMobile ? "visible" : "auto",
                  boxSizing: "border-box",
                }}
              >
                <RightPanel
                  selectedZone={selectedZone}
                  setSelectedZone={setSelectedZone}
                  zoneData={activeRightPanelData}
                  setActiveTabData={setActiveTabData}
                  setActiveSlot={setActiveSlot}
                  drawingMode={drawingMode}
                  setDrawingMode={setDrawingMode}
                  lines={lines}
                  setLines={setLines}
                  deleteLineZone={deleteLineZone}
                  chartType={chartType}
                  setChartType={setChartType}
                  totalCount={fullDayDataCount}
                  ttcTotal={ttcData?.count || 0}
                  petTotal={petData?.count || 0}
                  adsData={adsData}
                  currentAdIndex={currentAdIndex}
                  setCurrentAdIndex={setCurrentAdIndex}
                  parentActiveTab={activeTab}
                  isLineZoneData={isLineZoneData}
                />
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default Dashboard;